import { Mail, Phone, MapPin } from "lucide-react";
import { RevealText, Reveal } from "@/components/site/Reveal";
import { QuoteForm } from "@/components/site/QuoteForm";
import { IMAGES, LOCATIONS } from "@/lib/assets";

const INFO = [
  { icon: Phone, label: "24/7 Our Support", value: "+1 647 657 3905" },
  { icon: Mail, label: "Send Your Message", value: "info@freight-iq.com" },
  { icon: MapPin, label: "Location", value: "211 - 320 Matheson Blvd West, Mississauga, ON L5R 0H2, Canada" },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <section className="relative pt-40 pb-12 mx-auto max-w-[1600px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">/ Let's Get in Touch</p>
            <h1 className="font-display uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
              <RevealText lines={["Your business,", "our priority —", "worldwide."]} testid="page-title" />
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed">
                Have a question, need a quote, or looking for a trusted logistics partner? Our global team is here to help—quickly, clearly, and personally. Reach out anytime. We're ready to move your freight, and your business, forward.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="lg:col-span-5 w-full">
            <div className="flex flex-col gap-4">
              {[
                { img: IMAGES.air, label: "Air Cargo" },
                { img: IMAGES.oceanSunset, label: "Sea Freight" },
                { img: IMAGES.truckBlue, label: "Trucking & Warehousing" },
              ].map((m) => (
                <div key={m.label} className="relative h-40 sm:h-44 overflow-hidden border border-white/10 group">
                  <img src={m.img} alt={m.label} className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700 ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <span className="absolute bottom-4 left-4 font-mono text-[11px] uppercase tracking-widest text-white/90">{m.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 sm:px-8 pb-20 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-7">
          <h2 className="font-display text-2xl uppercase tracking-tight mb-2">Have a specific shipment or question in mind?</h2>
          <p className="text-white/50 mb-10">Fill out the form below and our team will get back to you within 24 hours.</p>
          <QuoteForm />
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="divide-y divide-white/10 border-y border-white/10">
            {INFO.map((c) => (
              <div key={c.label} className="py-6 flex items-start gap-4">
                <c.icon size={18} className="text-primary mt-1 shrink-0" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-white/40">{c.label}</p>
                  <p className="text-white/90 text-lg mt-1">{c.value}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-white/50 text-sm">Our support centre is available 24/7. Expect a tailored response within 24 hours of submitting your request.</p>
        </Reveal>
      </section>

      {/* Global offices */}
      <section className="border-t border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-20">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Global Offices</p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter">Find us around the world.</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {LOCATIONS.map((loc, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06} className="bg-[#080808] p-8">
                <MapPin size={18} className="text-primary" />
                <p className="font-display text-xl uppercase tracking-tight mt-4">{loc.country}</p>
                <p className="text-white/50 text-sm mt-2">{loc.detail}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
